/**
 * Fitness Assessment normalization helpers
 *
 * The backend enum (see /api/assessments/categories) defines a canonical set of
 * test names & categories. Some legacy/seeded records in the database use near
 * duplicates (e.g. "Lunges" vs canonical "Lunge", "20m Sprint" vs "Sprint 20m",
 * category "Endurance" vs canonical "Resistance"). When the progress chart
 * renders, it iterates canonical tests and looks up `progress[test]` — any
 * variant key is silently dropped and the card shows "No data recorded".
 *
 * These helpers make the frontend rendering pipeline tolerant of those
 * variants by normalizing keys at lookup time (without mutating seed data).
 */

// Collapse whitespace, punctuation and casing so "Bent-over Row" and
// "Bent Over Row" both map to "bentoverrow".
const normalize = (s) =>
  String(s || '')
    .toLowerCase()
    .replace(/[\s\-_/]+/g, '')
    .trim();

// Treat "20m Sprint" and "Sprint 20m" as equivalent (and similar permutations)
// by sorting the alphanumeric tokens.
const tokenKey = (s) =>
  String(s || '')
    .toLowerCase()
    .split(/[\s\-_/]+/)
    .filter(Boolean)
    .sort()
    .join('');

const CATEGORY_ALIASES = {
  // Legacy category name → new canonical (Phase 1 rename).
  resistance: 'Endurance',
};

// Explicit test name aliases (e.g. singular/plural mismatches that normalization
// alone cannot resolve). Keys are normalized variants → canonical names.
const TEST_NAME_ALIASES = {
  lunges: 'Lunge',
  // Phase 1 renames.
  backsquat: 'Squat',
};

/**
 * Given the raw `progress` object returned by
 * GET /api/assessments/athlete/{id}/history, return a new object keyed by the
 * canonical test names (using `categoriesMap` from /assessments/categories as
 * the source of truth). Categories inside each entry are also normalized so
 * the chart colors render.
 */
export function normalizeProgress(rawProgress, categoriesMap) {
  if (!rawProgress || !categoriesMap) return rawProgress || {};

  // Build canonical indexes from the enum
  const canonicalTests = [];
  Object.entries(categoriesMap).forEach(([cat, data]) => {
    (data?.tests || []).forEach((t) => canonicalTests.push({ test: t, category: cat }));
  });
  const byNorm = new Map(canonicalTests.map(({ test, category }) => [normalize(test), { test, category }]));
  const byTokens = new Map(canonicalTests.map(({ test, category }) => [tokenKey(test), { test, category }]));

  const out = {};
  Object.entries(rawProgress).forEach(([rawKey, entry]) => {
    const aliasName = TEST_NAME_ALIASES[normalize(rawKey)];
    const hit =
      (aliasName && byNorm.get(normalize(aliasName))) ||
      byNorm.get(normalize(rawKey)) ||
      byTokens.get(tokenKey(rawKey));
    const canonicalTest = hit ? hit.test : rawKey;
    const rawCategory = entry?.category;
    const canonicalCategory =
      (hit && hit.category) ||
      CATEGORY_ALIASES[normalize(rawCategory)] ||
      rawCategory;

    // Merge if canonical key already present (e.g. both "Lunge" and "Lunges" existed)
    if (out[canonicalTest]) {
      out[canonicalTest] = {
        ...out[canonicalTest],
        history: [...(out[canonicalTest].history || []), ...(entry?.history || [])].sort(
          (a, b) => String(a.date).localeCompare(String(b.date))
        ),
      };
    } else {
      out[canonicalTest] = { ...entry, category: canonicalCategory };
    }
  });

  return out;
}

/**
 * Normalize the `results` map returned by
 * GET /api/assessments/athlete/{id}/latest so that:
 *   - test_type is rewritten to its canonical name
 *   - results that were recorded under a non-canonical category
 *     (e.g. "Endurance") are moved under the canonical category ("Resistance")
 *     so the per-category grid on the Player page can find them.
 */
export function normalizeLatestResults(rawLatest, categoriesMap) {
  if (!rawLatest || !rawLatest.results || !categoriesMap) return rawLatest;

  const canonicalTests = [];
  Object.entries(categoriesMap).forEach(([cat, data]) => {
    (data?.tests || []).forEach((t) => canonicalTests.push({ test: t, category: cat }));
  });
  const byNorm = new Map(canonicalTests.map(({ test, category }) => [normalize(test), { test, category }]));
  const byTokens = new Map(canonicalTests.map(({ test, category }) => [tokenKey(test), { test, category }]));

  const normalizedResults = {};
  Object.entries(rawLatest.results).forEach(([rawCategory, list]) => {
    (list || []).forEach((r) => {
      const aliasName = TEST_NAME_ALIASES[normalize(r.test_type)];
      const hit =
        (aliasName && byNorm.get(normalize(aliasName))) ||
        byNorm.get(normalize(r.test_type)) ||
        byTokens.get(tokenKey(r.test_type));
      const canonicalTest = hit ? hit.test : r.test_type;
      const canonicalCategory =
        (hit && hit.category) ||
        CATEGORY_ALIASES[normalize(rawCategory)] ||
        rawCategory;
      if (!normalizedResults[canonicalCategory]) normalizedResults[canonicalCategory] = [];
      normalizedResults[canonicalCategory].push({
        ...r,
        test_type: canonicalTest,
        category: canonicalCategory,
      });
    });
  });

  const normalizedPrevious = {};
  Object.entries(rawLatest.previous_results || {}).forEach(([rawKey, val]) => {
    const aliasName = TEST_NAME_ALIASES[normalize(rawKey)];
    const hit =
      (aliasName && byNorm.get(normalize(aliasName))) ||
      byNorm.get(normalize(rawKey)) ||
      byTokens.get(tokenKey(rawKey));
    const canonicalTest = hit ? hit.test : rawKey;
    const valCategory = val?.category;
    const canonicalCategory =
      (hit && hit.category) ||
      CATEGORY_ALIASES[normalize(valCategory)] ||
      valCategory;
    normalizedPrevious[canonicalTest] = val ? { ...val, category: canonicalCategory } : val;
  });

  return { ...rawLatest, results: normalizedResults, previous_results: normalizedPrevious };
}
